import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { FieldArray } from 'formik'
import CustomButton from '../../../components/UI/Buttons/CustomButton'
import MainHeader from '../../../components/Calendar/Header'
import Checkbox from '../../../components/UI/Inputs/Checkbox'
import MultiselectInput from '../../../components/UI/Inputs/MultiselectInput'
import GeneralForm from '../../../components/Form'
import SelectInput from '../../../components/UI/Inputs/SelectInput'
import SelectedPostPopup, { SELECTED_POPUP_PAGE } from '../../../components/SelectedPostPopup'
import ConfirmPopup from '../../../components/UI/ConfirmPopup'
import CustomPopup from '../../../components/UI/Popup'
import TrashIcon from '../../../components/UI/Icons/Trash'
import TextInput from '../../../components/UI/Inputs/TextInput'

import { useDispatch, useSelector } from '../../../store/index'
import { TProjectUser, TGetRemoveUserRolesFromProjectPayload } from '../../../store/project/types'
import {
  getProjectUsersByRoleThunk,
  removeUserRolesFromProjectThunk
} from '../../../store/project/thunks'
import { getAgencyInvitationsThunk, inviteToProjectThunk } from '../../../store/agency/thunks'
import { TUserProjectInvitationPayload } from '../../../store/agency/types'
import { setHeaderView, toggleInviteModalAction } from '../../../store/application/slice'
import Roles from '../../../models/roles'

import ExampleAvatar from '../../../assets/img/example/story.svg'

import '../Team/Team.scss'
import { HEADER_VIEW } from '../../../store/application/types'

const transformRoleName = (role: Roles) => {
  const displayRole = role.replaceAll('-', ' ').split('')
  displayRole.splice(0, 1, displayRole[0].toLocaleUpperCase())
  return displayRole.join('')
}

interface ClientProps { }

const Client: FC<ClientProps> = (props) => {
  const dispatch = useDispatch()
  const { projectId, projectUsers, isInviteModalOpen, loaders } = useSelector((state) => ({
    projectId: state.project.currentProject.id,
    projectUsers: state.project.currentProject.projectUsers,
    isInviteModalOpen: state.app.isInviteModalOpen,
    loaders: state.project.loaders
  }))

  const [checkedUsers, setCheckedUsers] = useState<number[]>([])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [invitationInProgress, setInvitationInProgress] = useState<boolean>(false)
  const [deletedUser, setDeletedUser] = useState<TProjectUser | null>(null)
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState<boolean>(false)

  useEffect(() => {
    const getProjectUsers = async () => {
      await dispatch(getProjectUsersByRoleThunk())
    }
    getProjectUsers()
  }, [projectId])

  const projectUserExternalApprover = useMemo(
    () => projectUsers.filter(({ role }) => role === Roles.EXTERNAL_APPROVER),
    [projectUsers]
  )

  useEffect(() => {
    dispatch(setHeaderView(HEADER_VIEW.CLIENT))
  }, [])

  const checkUncheckUser = (id: number) => {
    const isCheckedIndex = checkedUsers.findIndex((existedId) => id === existedId)
    if (isCheckedIndex === -1) {
      setCheckedUsers((prevState) => [...prevState, id])
    } else {
      setCheckedUsers((prevState) => {
        const localCopy = [...prevState]
        localCopy.splice(isCheckedIndex, 1)
        return localCopy
      })
    }
  }

  const setUpFormSelect = {
    initialValues: { name: '', timezone: '' },
    validation: 'project',
    onSubmit: async () => { }
  }

  const setUpFormMultiSelect = {
    initialValues: {
      invite_to_project: [
        {
          name: '',
          email: '',
          project_role: Roles.EXTERNAL_APPROVER
        }
      ]
    },
    validation: 'invite_members',
    validateOnBlur: false,
    onSubmit: async (values: any) => {
      setInvitationInProgress(true)

      const { invite_to_project: inviteToProject } = values

      const newMembers = inviteToProject.map((user: any) => {
        const payload: TUserProjectInvitationPayload = {
          project_id: projectId,
          email: user.email,
          name: user.name,
          project_role: user.project_role
        }
        return payload
      })

      await Promise.all(
        newMembers.map(
          async (payload: TUserProjectInvitationPayload) =>
            await dispatch(inviteToProjectThunk(payload))
        )
      )
      setInvitationInProgress(false)
      await dispatch(getProjectUsersByRoleThunk())
      dispatch(toggleInviteModalAction())
    }
  }

  const onCloseDeleteModal = () => {
    setIsOpenConfirmModal(false)
  }

  const onConfirmDeleteModal = async () => {
    onCloseDeleteModal()
    const { id: user_id } = deletedUser!
    const payload: TGetRemoveUserRolesFromProjectPayload = {
      user_id: '' + user_id,
      project_id: '' + projectId
    }
    await dispatch(removeUserRolesFromProjectThunk(payload))
    setDeletedUser(null)
  }

  const onCloseInviteModal = () => {
    dispatch(toggleInviteModalAction())
  }

  const onDelete = (user: TProjectUser) => {
    setIsOpenConfirmModal(true)
    setDeletedUser(user)
  }

  const isDeletingUser = useCallback(
    (userId: number) => {
      return deletedUser?.id === userId && loaders.users.delete
    },
    [deletedUser?.id, loaders.users.delete]
  )

  return (
    <div className='team-page-wrapper'>
      <MainHeader />
      <div className='team-page-container'>
        <div className='team-user-list'>
          {!!projectUserExternalApprover.length ? (
            projectUserExternalApprover.map((user) => (
              <div
                key={user.id}
                className={`team-user-list_user ${checkedUsers.find((id) => id === user.id) && 'team-user-list_user_checked'
                  }`}
              >
                <div className='team-user-list_user-checkbox'>
                  <Checkbox
                    checked={!!checkedUsers.find((id) => id === user.id)}
                    onChange={() => { }}
                  />
                </div>
                <div className='team-user-list_user-avatar'>
                  <img src={ExampleAvatar} alt='' />
                </div>
                <div className='team-user-list_user-info'>
                  <div className='team-user-list_user-info-name'>{user.name}</div>
                  <div className='team-user-list_user-info-email' title={user.email}>
                    {user.email}
                  </div>
                </div>
                <div className='team-user-list_user-role-select'>
                  <GeneralForm {...setUpFormSelect}>
                    <SelectInput
                      label={user.role}
                      name='role'
                      placeholder='Select a user role'
                      disabled={true}
                      options={[{ label: transformRoleName(user.role), value: user.role }]}
                      defaultValue={{ label: transformRoleName(user.role), value: user.role }}
                    />
                  </GeneralForm>
                </div>
                <div className='team-user-list_user-remove'>
                  <CustomButton
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete(user)
                    }}
                    inProgress={isDeletingUser(user.id)}
                    button='delete'
                  >
                    {!isDeletingUser(user.id) && <TrashIcon />}
                  </CustomButton>
                </div>
              </div>
            ))
          ) : (
            <div className='team-user-list__empty'>There are no clients added to this project</div>
          )}
        </div>
        <SelectedPostPopup
          {...{
            selectedPostIds: checkedUsers,
            page: SELECTED_POPUP_PAGE.TEAM,
            showDeleteModal: () => setIsDeleteModalOpen(true),
            setSelectedPosts: (userIds: number[]) => setCheckedUsers(userIds)
          }}
        />
        <ConfirmPopup
          onConfirm={onConfirmDeleteModal}
          onClose={onCloseDeleteModal}
          isModalOpen={isOpenConfirmModal}
        >
          Are you sure you want to delete the content?
        </ConfirmPopup>

        <CustomPopup
          open={isInviteModalOpen}
          buttonClose={onCloseInviteModal}
          closeOnDocumentClick={false}
          className='invite-modal'
        >
          <div className='invite-modal-wrapper'>
            <div className='invite-modal_header'>Invite external approvers to your project</div>
            <div className='invite-modal_multi-select'>
              <GeneralForm {...setUpFormMultiSelect}>
                <div className='agency-creation-step__invite-rows'>
                  <FieldArray
                    name='invite_to_project'
                    render={({ push, remove, form: { values, ...formRest }, ...rest }) => (
                      <>
                        {values.invite_to_project &&
                          values.invite_to_project.map((user: any, index: number) => (
                            <div
                              key={`new-user-${index}`}
                              className='agency-creation-step__invite-row'
                            >
                              <TextInput
                                label='Name'
                                name={`invite_to_project.${index}.name`}
                                placeholder='Name'
                              />
                              <TextInput
                                label='Email'
                                name={`invite_to_project.${index}.email`}
                                placeholder='Email'
                              />
                              <SelectInput
                                label='Project role'
                                name={`invite_to_project.${index}.project_role`}
                                placeholder='Role'
                                disabled={true}
                                options={[
                                  {
                                    label: 'External Approver',
                                    value: `${Roles.EXTERNAL_APPROVER}`
                                  }
                                ]}
                                defaultValue={{
                                  label: 'External Approver',
                                  value: `${Roles.EXTERNAL_APPROVER}`
                                }}
                              />
                              <CustomButton
                                type='button'
                                className='agency-creation-step__invite-row__delete'
                                button='delete'
                                onClick={() => remove(index)}
                              >
                                <TrashIcon />
                              </CustomButton>
                            </div>
                          ))}
                        <CustomButton
                          type='button'
                          className='agency-creation-step__button-add'
                          button='subtle'
                          onClick={() =>
                            push({ name: '', email: '', project_role: Roles.EXTERNAL_APPROVER })
                          }
                        >
                          Add Member
                        </CustomButton>
                      </>
                    )}
                  />
                </div>
                <div className='invite-modal_actions'>
                  <CustomButton type='button' button='secondary' onClick={onCloseInviteModal}>
                    Cancel
                  </CustomButton>

                  <CustomButton inProgress={invitationInProgress} button='primary' type='submit'>
                    Invite
                  </CustomButton>
                </div>
              </GeneralForm>
            </div>
          </div>
        </CustomPopup>
      </div>
    </div>
  )
}

export default Client
